// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LineNotes from "@bg/App/Components/MyCustomQuotes/LineNotes";
import { styles } from "@bg/App/Components/MyCustomQuotes/MyCustomQuotesQuoteLine";
import deleteLine from "@bg/App/Handlers/CustomQuote/DeleteCustomQuoteLine";
import updateLineNotes from "@bg/App/Handlers/CustomQuote/UpdateLineNotes";
import updateLinePrice from "@bg/App/Handlers/CustomQuote/UpdateLinePrice";
import updateLineQty from "@bg/App/Handlers/CustomQuote/UpdateLineQty";
import { CustomQuoteModel, GroupLineModel } from "@bg/App/Services/CustomQuoteService";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { printItems } from "@bg/App/Utilities/PrintItemsQrCode";
import ProductAddToCartButton from "@bg/Overrides/Components/ProductAddToCartButton";
import ProductContextAvailability from "@bg/Overrides/Components/ProductContextAvailability";
import ProductDescription from "@bg/Overrides/Components/ProductDescription";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductImage from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { QRCode } from "react-qrcode-logo";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: CustomQuoteModel;
    quoteLine: GroupLineModel;
    groupName: string;
    parentQuoteLineId: string;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const productInfo: ProductInfo = state.myCustomQuoteDetails.productsInfo[ownProps.quoteLine.lineId];
    return {
        settingsCollection: getSettingsCollection(state),
        productInfo,
        realTimeInventoryFailed: state.myCustomQuotes.realTimeInventoryFailed,
        realTimePricingFailed: state.myCustomQuotes.realTimePricingFailed,
    };
};

const mapDispatchToProps = {
    updateLineNotes,
    updateLineQty,
    deleteLine,
    updateLinePrice,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MyCustomQuotesRelatedLine: React.FC<Props> = ({
    quote,
    quoteLine,
    settingsCollection,
    groupName,
    productInfo,
    realTimeInventoryFailed,
    realTimePricingFailed,
    updateLineNotes,
    updateLineQty,
    deleteLine,
    updateLinePrice,
    parentQuoteLineId,
}) => {
    const product = quoteLine.productResult;
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = useState(false);
    const [showLineNote, setShowLineNotes] = useState(!!quoteLine.notes);
    const [quotePrice, setQuotePrice] = useState(quoteLine.extendedPrice);
    const [calculateMarginModalIsOpen, setCalculateMarginModalIsOpen] = useState(false);
    const [marginPercent, setMarginPercent] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);

    const dispatch = useAppDispatch();

    // JS calculates margin 2.55*100 = 254.99996
    // parse it as float with toFixed(2) to do the round
    useEffect(() => {
        if (!quoteLine) {
            return;
        }
        setMarginPercent(parseFloat((quoteLine.margin * 100).toFixed(2)).toString());
        setQuotePrice(quoteLine.extendedPrice);
    }, [quoteLine]);

    const deleteQuotetLine = () => {
        setIsDeleting(true);

        deleteLine({
            customQuoteId: quote.id,
            lineId: quoteLine.lineId,
            onComplete: result => {
                if (result.successful) {
                    setIsDeleting(false);
                    setDeleteLineModalIsOpen(false);
                }
            },
        });
    };

    const toggleLineNotes = () => {
        setShowLineNotes(!showLineNote);
    };

    const updateQuoteLineNotes = (notes: string) => {
        if (notes === quoteLine.notes) {
            return;
        }
        updateLineNotes({
            customQuoteId: quote.id,
            linesModel: [{ lineId: quoteLine.lineId, Note: notes }],
            updateNote: true,
            onComplete: (result: any) => {
                setShowLineNotes(!!notes);
            },
        });
    };

    const onQuotePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value) {
            setQuotePrice("");
            return;
        }
        setQuotePrice(event.target.value);
    };

    const onQuotePriceBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const price = parseFloat(event.target.value);
        if (Number.isNaN(price)) {
            setQuotePrice(quoteLine.extendedPrice);
            return;
        }
        setQuotePrice(price.toFixed(2).toString());
        if (quoteLine.extendedPrice && event.target.value === quoteLine.extendedPrice.toString()) {
            return;
        }
        updateLinePrice({
            customQuoteId: quote.id,
            updatePrice: true,
            linesModel: [{ lineId: quoteLine.lineId, price: Number(price.toFixed(2)) }],
        });
    };

    const handleMarginChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!/^[0-9\b]+$/.test(event.target.value) && event.target.value !== "") {
            return;
        }
        setMarginPercent(event.target.value);
    };

    const handleMarginBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const price = parseInt(event.target.value, 10);
        if (Number.isNaN(price)) {
            setMarginPercent("0");
            return;
        }
        setMarginPercent(price.toString());
    };

    // JS calculates margin 2.55*100 = 254.99996
    // parse it as float with toFixed(2) to do the round
    const handleCloseCalculateMarginModal = () => {
        setMarginPercent(parseFloat((quoteLine.margin * 100).toFixed(2)).toString());
        setCalculateMarginModalIsOpen(false);
    };

    const applyMargin = () => {
        if (!productInfo) {
            return;
        }
        setCalculateMarginModalIsOpen(false);
        const margin = parseFloat(marginPercent) / 100;
        const price = quoteLine.extendedPrice ? Number(quoteLine.extendedPrice) : productInfo?.pricing?.unitNetPrice;
        updateLinePrice({
            customQuoteId: quote.id,
            updateMargin: true,
            margin,
            updatePrice: !quoteLine.extendedPrice ? true : undefined,
            linesModel: [{ lineId: quoteLine.lineId, price }],
            onSuccess: result => {
                const parentQuoteLine = result.quote.groupLinesModel[groupName].find(
                    (line: GroupLineModel) => line.lineId === parentQuoteLineId,
                );

                const updatedRelatedItem = parentQuoteLine.relatedItems[quoteLine.lineId];
                if (updatedRelatedItem) {
                    setQuotePrice(updatedRelatedItem.extendedPrice);
                }
            },
        });
    };

    const onQtyOrderedChanged = (qty: number) => {
        dispatch({
            type: "Pages/MyCustomQuotes/UpdateProductsQty",
            lineModel: {
                lineId: quoteLine.lineId,
                qty,
                originalQty: quoteLine.qty,
            },
        });
    };

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo: { ...productInfo, qtyOrdered: quoteLine.qty },
        onQtyOrderedChanged,
        onUnitOfMeasureChanged: unitOfMeasure => {},
    };

    // print line qr code
    const printItem = () => {
        printItems(`#qrCode_${quoteLine.lineId} canvas`, quoteLine.productResult, [productInfo!], true, true);
    };

    if (!productInfo) {
        return null;
    }

    const lineLabel = showLineNote ? "Hide Notes" : quoteLine.notes.length ? "Show Notes" : "Line Notes";
    const qtyOutsideBreakPrice =
        quote.isCameFromNormalQuote && quoteLine.breakPricesRfqModel && !quoteLine.customerPrice;

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper
                {...styles.lineInnerContainer}
                id={`contentItem_${quoteLine.lineId}`}
                data-test-selector={`${product.id}`}
                className="related-item"
            >
                <StyledWrapper {...styles.leftSection}>
                    <StyledWrapper {...styles.imageGridItem} className="related-item">
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </StyledWrapper>

                    <StyledWrapper {...styles.descriptionGridItem}>
                        <ProductDescription
                            product={productContext}
                            lines={2}
                            lineHeight={22}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                        <StyledWrapper {...styles.partNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={product.detail?.sku || product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        </StyledWrapper>
                        {!quoteLine.isSpecItem && (
                            <StyledWrapper {...styles.availabilityGridItem}>
                                {productSettings.showInventoryAvailability && (
                                    <ProductContextAvailability
                                        extendedStyles={styles.productAvailabilityStyles as any}
                                    />
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.rightSection}>
                    <StyledWrapper {...styles.rightSectionTop}>
                        <StyledWrapper {...styles.priceAndAvailabilityInnerContainer}>
                            <StyledWrapper {...styles.priceGridItem}>
                                <legend className="custom-quote">{translate("My Price")}</legend>
                                {!quoteLine.breakPricesRfqModel &&
                                !quoteLine.customerPrice &&
                                productInfo.pricing?.additionalResults?.["noPriceFound"]?.toLowerCase() !== "true" ? (
                                    <ProductPrice
                                        product={productContext}
                                        showSavings={false}
                                        extendedStyles={styles.productPriceStyles as any}
                                    />
                                ) : (
                                    <Typography
                                        variant="h6"
                                        weight={fontConfig.weight.bold}
                                        css={css`
                                            width: 100%;
                                            text-align: left;
                                            margin-bottom: 0;
                                            @media (max-width: 1024px) {
                                                text-align: left;
                                            }
                                            legend {
                                                font-size: ${fontConfig.fontSize.legend}px;
                                                font-weight: ${fontConfig.weight.bold};
                                                text-transform: uppercase;
                                            }
                                        `}
                                    >
                                        {quoteLine.customerPrice &&
                                        (quoteLine.breakPricesRfqModel || quoteLine.isSpecItem) ? (
                                            <LocalizedCurrency amount={quoteLine.customerPrice} />
                                        ) : (
                                            "N/A"
                                        )}
                                    </Typography>
                                )}
                            </StyledWrapper>

                            <Hidden {...styles.mobileHiddenActions}>
                                <StyledWrapper {...styles.marginWrapper}>
                                    <legend>{translate("Quote Price")}</legend>
                                    <StyledWrapper {...styles.quotePriceInput}>
                                        <label>{translate("$")}</label>
                                        <TextField
                                            type="text"
                                            value={quotePrice}
                                            onChange={onQuotePriceChange}
                                            onBlur={onQuotePriceBlur}
                                            maxLength={10}
                                        />
                                        <label>/{product.unitOfMeasures?.[0].unitOfMeasureDisplay}</label>
                                    </StyledWrapper>
                                </StyledWrapper>
                                <Link {...styles.calculateMargin} onClick={() => setCalculateMarginModalIsOpen(true)}>
                                    {translate("Calculate Margin")}
                                </Link>
                            </Hidden>

                            <StyledWrapper {...styles.uomAndQuantityInnerContainer}>
                                {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                    <StyledWrapper {...styles.uomGridItem}>
                                        <ProductUnitOfMeasureSelect
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.quantityGridItem}>
                                    <ProductQuantityOrdered
                                        {...({ changeOnBlur: true } as any)}
                                        extendedStyles={styles.productQuantityOrderedStyles as any}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.lineTotal}>
                                <Typography variant="legend">{translate("Total")}</Typography>
                                <Typography {...styles.priceTotal}>
                                    <LocalizedCurrency amount={quoteLine.totalQuotePrice} />
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.buttonsGridItem}
                            className={quote.isCameFromNormalQuote ? "flex-grow" : ""}
                        >
                            {!quote.isCameFromNormalQuote && (
                                <ProductAddToCartButton
                                    notes={quoteLine.notes}
                                    iconOnly={false}
                                    sizeVariant="small"
                                    extendedStyles={styles.addToCartButton}
                                    data-test-selector="addToCart"
                                />
                            )}

                            <Button
                                {...styles.deleteLink}
                                onClick={() => setDeleteLineModalIsOpen(true)}
                                data-test-selector="deleteListItem"
                            >
                                <ButtonIcon src="Delete" size={18} color="secondary" />
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    {productInfo.pricing?.additionalResults?.["noPriceFound"]?.toLowerCase() === "true" && (
                        <StyledWrapper
                            css={css`
                                @media (max-width: 481px) {
                                    margin-top: 8px;
                                }
                            `}
                        >
                            <Typography
                                color="danger"
                                size={fontConfig.fontSize.xSmall}
                                lineHeight={fontConfig.lineHeight.singleLineSmall}
                            >
                                {siteMessage("Custom_Price_FailedToLoad")}
                            </Typography>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.marginAndMsgWrapper}>
                        <StyledWrapper {...styles.marginWrapper} className="hide-on-small">
                            <legend>{translate("Quote Price")}</legend>
                            <StyledWrapper {...styles.quotePriceInput}>
                                <label>{translate("$")}</label>
                                <TextField
                                    type="text"
                                    value={quotePrice}
                                    onChange={onQuotePriceChange}
                                    onBlur={onQuotePriceBlur}
                                    maxLength={10}
                                />
                                <label>/{product.unitOfMeasures?.[0].unitOfMeasureDisplay}</label>
                            </StyledWrapper>
                        </StyledWrapper>
                        {qtyOutsideBreakPrice && (
                            <Typography size={12} color="danger" {...styles.errorMsg}>
                                {siteMessage("Custom_CustomQuote_SpecificRangeProduct")}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.notesGridItem}>
                        <Link
                            {...styles.calculateMargin}
                            className="hide-on-small"
                            onClick={() => setCalculateMarginModalIsOpen(true)}
                        >
                            {translate("Calculate Margin")}
                        </Link>
                        <Link {...styles.printQrCode} onClick={printItem}>
                            {translate("Print QR Code")}
                        </Link>
                        <Link
                            {...styles.toggleLink}
                            onClick={toggleLineNotes}
                            data-test-selector="customQuoteLine_notesShowHideButton"
                        >
                            {translate(lineLabel)}
                        </Link>

                        <LineNotes
                            showNotes={showLineNote}
                            notes={quoteLine.notes}
                            onNotesChange={updateQuoteLineNotes}
                        />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        display: none;
                    `}
                    className="canvas-wrapper"
                    id={`qrCode_${quoteLine.lineId}`}
                >
                    <QRCode
                        value={`${window.location.origin}${product.canonicalUrl}`}
                        logoWidth={250}
                        logoHeight={250}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                size={400}
                isOpen={calculateMarginModalIsOpen}
                handleClose={handleCloseCalculateMarginModal}
                headline={translate("Add Price Margin")}
            >
                <TextField
                    type="text"
                    label={translate("Margin %")}
                    value={marginPercent}
                    onChange={handleMarginChange}
                    onBlur={handleMarginBlur}
                />
                <StyledWrapper className="modal-actions">
                    <Button onClick={applyMargin}>{translate("Apply")}</Button>
                    <Button variant="secondary" onClick={handleCloseCalculateMarginModal}>
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete List Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteLineModalIsOpen(false)}
                onSubmit={deleteQuotetLine}
                submitTestSelector="submitDeleteListItem"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyCustomQuotesRelatedLine);
